import React, { FC, PropsWithChildren, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
    AuthenticationService,
    HelperButton,
    LanguageSelector,
    TextField,
} from '@eta/components';
import {
    ActionButton,
    DefaultButton,
    Icon,
    PrimaryButton,
    Spinner,
    SpinnerSize,
} from '@fluentui/react';
import { useTranslation } from 'react-i18next';
import { ApiException, ChangePasswordDto } from '../Service/proxy';
import { ServiceCreator } from '../Service/client';
import './ChangePassword.scss';
import '../../WaveWave.svg';
import { UIHelpers } from '../../helpers/UIHelpers';

export interface ChangePasswordProps {
    authService: AuthenticationService;
    applicationLogo?: string;
}
const ChangePassword: FC<PropsWithChildren<ChangePasswordProps>> = (
    props: PropsWithChildren<ChangePasswordProps>
) => {
    const { authService, applicationLogo, children } = props;
    const { t } = useTranslation(['common']);
    const [oldPassword, setOldPassword] = useState<string>('');
    const [newPassword, setNewPassword] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [retypeNewPassword, setRetypeNewPassword] = useState<string>('');
    const [isFormDetailsValid, setIsFormDetailsValid] = useState<boolean>(true);
    const [
        isChangePasswordSuccess,
        setIsChangePasswordSuccess,
    ] = useState<boolean>(false);
    const [
        oldPasswordErrorMessage,
        setOldPasswordErrorMessage,
    ] = useState<string>('');
    const [
        newPasswordErrorMessage,
        setNewPasswordErrorMessage,
    ] = useState<string>('');
    const [
        retypeNewPasswordErrorMessage,
        setRetypeNewPasswordErrorMessage,
    ] = useState<string>('');
    const pattern = new RegExp(
        /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&_*]{8,}$/
    );

    const history = useHistory();
    const location = useLocation();

    const onResetPasswordClicked = async () => {
        setIsLoading(true);
        if (isFormDetailsValid) {
            try {
                const dto = new ChangePasswordDto();
                dto.oldPassword = oldPassword;
                dto.newPassword = newPassword;
                dto.confirmPassword = retypeNewPassword;

                const client = ServiceCreator.getUserProfileAdminClient();
                const response = await client.changePassword(dto);
                setIsChangePasswordSuccess(
                    response.isSuccess !== undefined
                        ? response.isSuccess
                        : false
                );
                if (!response?.isSuccess) {
                    setIsChangePasswordSuccess(false);
                }
                setIsLoading(false);
            } catch (ex) {
                const ex1 = ex as ApiException;
                const errorFromAPI = UIHelpers.GetErrors(ex1);
                const pwdMismatch = errorFromAPI.PasswordMismatch;
                if (ex.status === 400 && pwdMismatch !== null) {
                    setOldPasswordErrorMessage(t('oldPasswordError'));
                } else {
                    setOldPasswordErrorMessage(
                        errorFromAPI[Object.keys(errorFromAPI)[0]]
                    );
                }
                setIsChangePasswordSuccess(false);
                setIsLoading(false);
            }
        }
    };

    const allTextFieldsFilled = () => {
        const val =
            oldPassword.length > 0 &&
            newPassword.length > 0 &&
            retypeNewPassword.length > 0;
        return val !== undefined ? val : false;
    };

    const handleOnChangeOldPassword = (oldPasswordText: string | undefined) => {
        if (oldPasswordText !== undefined) {
            setOldPassword(oldPasswordText);
        }
    };

    const handleOnChangeNewPassword = (newPasswordText: string | undefined) => {
        setNewPassword(newPasswordText !== undefined ? newPasswordText : '');
        if (newPasswordText !== undefined && newPasswordText?.length < 8) {
            setNewPasswordErrorMessage(t('newPasswordLengthError'));
            setIsFormDetailsValid(false);
        } else if (
            newPasswordText !== undefined &&
            !pattern.test(newPasswordText)
        ) {
            setNewPasswordErrorMessage(t('newPasswordCharactersError'));
            setIsFormDetailsValid(false);
        } else {
            setIsFormDetailsValid(true);
            setNewPasswordErrorMessage('');
            setNewPassword(
                newPasswordText !== undefined ? newPasswordText : ''
            );
        }
        if (
            newPasswordText !== undefined &&
            retypeNewPassword !== '' &&
            newPasswordText !== retypeNewPassword
        ) {
            setRetypeNewPasswordErrorMessage(t('retypePasswordError'));
            setIsFormDetailsValid(false);
        }
    };

    const handleOnChangeConfirmPassword = (
        confirmPassword: string | undefined
    ) => {
        setRetypeNewPassword(
            confirmPassword !== undefined ? confirmPassword : ''
        );
        if (confirmPassword !== undefined && confirmPassword !== newPassword) {
            setRetypeNewPasswordErrorMessage(t('retypePasswordError'));
            setIsFormDetailsValid(false);
        } else {
            setIsFormDetailsValid(true);
            setRetypeNewPasswordErrorMessage('');
            setRetypeNewPassword(
                confirmPassword !== undefined ? confirmPassword : ''
            );
        }
    };

    const onCancelClicked = async () => {
        history.go(-1);
    };

    const onSignInClicked = async () => {
        // const user = authService.currentUser;
        await authService.userManager.removeUser().catch((reason) => {
            console.error(reason);
        });

        await authService.loginCallback(location.hash);
    };

    return children ? (
        <>{children}</>
    ) : (
        <div className="changePasswordPage">
            <div className="rightSelectLang">
                <LanguageSelector />
            </div>
            <div className="cP-page">
                <img
                    className="img-logo"
                    src={applicationLogo}
                    alt="Application logo"
                />
                <div className="panel">
                    {!isChangePasswordSuccess && (
                        <div className="changePasswordContent">
                            <div className="changePasswordHeader">
                                <h3>
                                    <Icon iconName="Permissions" />{' '}
                                    {t('changePassword')}
                                </h3>
                            </div>
                            <div className="oldPwdComponents">
                                <div className="labelWithoutIcon">
                                    <div className="labelTxt">
                                        {t('oldPasswordLabel')}
                                    </div>
                                </div>
                                <div>
                                    <TextField
                                        value={oldPassword}
                                        autoFocus
                                        required
                                        placeholder={t('oldPasswordLabel')}
                                        onChange={(_, v: string | undefined) =>
                                            handleOnChangeOldPassword(v)
                                        }
                                        errorMessage={
                                            oldPasswordErrorMessage ?? ''
                                        }
                                        type="password"
                                        canRevealPassword
                                    />
                                </div>
                            </div>
                            <br />
                            <div className="lineSeparator" />
                            <br />
                            <div className="newPwdComponents">
                                <div className="labelWithIcon">
                                    <div className="labelTxt">
                                        {t('newPasswordLabel')}
                                    </div>

                                    <div className="newPwdHelpIcon">
                                        <div className="rightAlign">
                                            <HelperButton>
                                                {t('newPasswordDesc')}
                                            </HelperButton>
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    <TextField
                                        value={newPassword}
                                        required
                                        placeholder={t('newPasswordLabel')}
                                        errorMessage={
                                            newPasswordErrorMessage ?? ''
                                        }
                                        type="password"
                                        onChange={(_, v: string | undefined) =>
                                            handleOnChangeNewPassword(v)
                                        }
                                        canRevealPassword
                                    />
                                </div>
                            </div>
                            <br />
                            <div className="confirmPwdComponents">
                                <div className="labelWithoutIcon">
                                    <div className="labelTxt">
                                        {t('retypeNewPasswordLabel')}
                                    </div>
                                </div>
                                <div>
                                    <TextField
                                        value={retypeNewPassword}
                                        required
                                        placeholder={t(
                                            'retypeNewPasswordLabel'
                                        )}
                                        errorMessage={
                                            retypeNewPasswordErrorMessage ?? ''
                                        }
                                        type="password"
                                        onChange={(_, v: string | undefined) =>
                                            handleOnChangeConfirmPassword(v)
                                        }
                                        canRevealPassword
                                    />
                                </div>
                            </div>
                            <br />
                            <br />
                            <div className="btn_ChangePassword">
                                <DefaultButton
                                    className="btn-cancel"
                                    onClick={onCancelClicked}
                                    text={t('common:cancel')}
                                />
                                <PrimaryButton
                                    className="btn-save"
                                    onClick={onResetPasswordClicked}
                                    disabled={!allTextFieldsFilled() || isLoading || newPassword !== retypeNewPassword || newPasswordErrorMessage !== ''}
                                    text={t('common:resetPassword')}
                                >
                                     {isLoading && (
                                    <Spinner size={SpinnerSize.small} />
                                )}
                                    </PrimaryButton>
                            </div>
                            <div className="wavePattern">
                                <img src="/WaveWave.svg" alt="Wave" />
                            </div>
                        </div>
                    )}
                    {isChangePasswordSuccess && (
                        <div className="changePasswordSuccessContent">
                            <div className="changePasswordSuccessHeader send-header">
                                <h2>
                                    <Icon iconName="SkypeCircleCheck" />{' '}
                                    {t('thankYou')}
                                </h2>
                            </div>
                            <div className="labelSuccessTxt">
                                {t('changePasswordSuccess')}
                            </div>
                            <br />
                            <br />
                            <div className="labelContinueTxt">
                                {t('continueToPortal')}
                            </div>
                            <ActionButton
                                className="btn-Sign"
                                onClick={onSignInClicked}
                                text={t('common:signIn')}
                            />
                            <div>
                                <img src="/WaveWave.svg" alt="Wave" />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
export default ChangePassword;
